/**
 * 인증 API
 * Firebase 인증 연동 및 세션 관리
 *
 * @see BE #129 - Firebase Authentication 구조 재정리
 * @see routes/customer-api.php
 */

import { apiClient } from './client';
import type { FirebaseLoginRequest, FirebaseLoginResponse, LogoutResponse } from './types';

/**
 * 인증 API 서비스
 */
export const authApi = {
  /**
   * Firebase 로그인
   * Firebase ID Token을 백엔드로 전송하여 세션 확립
   *
   * 1. Sanctum CSRF 쿠키 초기화
   * 2. Firebase ID Token으로 백엔드 로그인
   * 3. Sanctum 세션 쿠키 수신
   *
   * POST /api/customer/auth/firebase/login
   *
   * @param idToken Firebase ID Token
   * @returns 로그인 결과 및 사용자 정보
   */
  firebaseLogin: async (idToken: string, signal?: AbortSignal): Promise<FirebaseLoginResponse> => {
    // Sanctum SPA 인증을 위한 CSRF 쿠키 초기화
    await apiClient.initCsrf();

    if (signal?.aborted) throw new DOMException('Login superseded', 'AbortError');
    const request: FirebaseLoginRequest = { idToken };
    return apiClient.post<FirebaseLoginResponse>('/api/customer/auth/firebase/login', request, { signal });
  },

  /**
   * 로그아웃
   * 서버 세션 종료 및 CSRF 상태 리셋
   *
   * POST /api/customer/auth/logout
   *
   * @returns 로그아웃 결과
   */
  logout: async (): Promise<LogoutResponse> => {
    const response = await apiClient.post<LogoutResponse>('/api/customer/auth/logout');
    // CSRF 상태 리셋 (다음 로그인 시 재초기화)
    apiClient.resetCsrf();
    return response;
  },
};
